import React, { Component } from 'react';
import { array } from 'prop-types';
import { Table, Input, Row, Col, Card, Tag, Radio } from 'antd';
import {
  SearchOutlined, TableOutlined, AppstoreOutlined, UserOutlined,
  WarningOutlined
} from '@ant-design/icons';

import { history } from '../../common/custom-router';
import { localeCompareSorter, numberCompareSorter } from '../../../utils/sorters';
import { includesFilter } from '../../../utils/filters';

import './index.scss';

const STATUS_CONFIG = {
  en_curso: { color: 'blue', label: 'En curso' },
  pendiente: { color: 'orange', label: 'Pendiente' },
  finalizado: { color: 'green', label: 'Finalizado' },
  sin_operaciones: { color: 'default', label: 'Sin operaciones' }
};

class ClientsList extends Component {
  static propTypes = {
    clients: array
  };

  state = {
    filter: '',
    viewMode: 'table'
  };

  tableColumns = () => [
    {
      title: 'RUT',
      dataIndex: 'rut',
      sorter: localeCompareSorter(({ rut }) => rut),
      width: 130
    },
    {
      title: 'Nombre',
      key: 'name',
      sorter: localeCompareSorter(({ firstName, lastName }) => `${firstName} ${lastName}`),
      render: (_, { firstName, lastName }) => `${firstName} ${lastName}`
    },
    {
      title: 'Teléfono',
      dataIndex: 'phoneNumber',
      width: 140
    },
    {
      title: 'Email',
      dataIndex: 'email',
      sorter: localeCompareSorter(({ email }) => email),
      ellipsis: true
    },
    {
      title: 'Operaciones',
      dataIndex: 'saleProcessesCount',
      sorter: numberCompareSorter(({ saleProcessesCount }) => saleProcessesCount),
      width: 120,
      render: (count) => (
        <Tag color={count > 0 ? 'blue' : 'default'}>{count || 0}</Tag>
      )
    },
    {
      title: 'Activas',
      dataIndex: 'activeSaleProcessesCount',
      sorter: numberCompareSorter(({ activeSaleProcessesCount }) => activeSaleProcessesCount),
      width: 100,
      render: (count) => (
        <Tag color={count > 0 ? 'green' : 'default'}>{count || 0}</Tag>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'overallStatus',
      width: 130,
      filters: Object.entries(STATUS_CONFIG).map(([value, { label }]) => ({
        text: label,
        value
      })),
      onFilter: (value, record) => record.overallStatus === value,
      render: (status, record) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.sin_operaciones;
        return (
          <span>
            <Tag color={config.color}>{config.label}</Tag>
            {record.hasOverdue && (
              <WarningOutlined style={{ color: '#faad14', marginLeft: 4 }} />
            )}
          </span>
        );
      }
    },
    {
      title: 'Tags',
      dataIndex: 'allTags',
      width: 180,
      render: (tags) => {
        if (!tags || tags.length === 0) return '-';
        return tags.slice(0, 3).map((tag) => (
          <Tag key={tag} style={{ marginBottom: 2 }}>{tag}</Tag>
        ));
      }
    }
  ];

  renderCards = (records) => {
    return (
      <Row gutter={[16, 16]}>
        {records.map((client) => (
          <Col key={client.id} xs={24} sm={12} md={8} lg={6}>
            <Card
              hoverable
              className="client-card"
              onClick={() => history.push(`/staff/clients/${client.id}`)}
            >
              <div className="client-card-header">
                <div className="client-avatar">
                  <UserOutlined />
                </div>
                <div className="client-info">
                  <h4>{client.firstName} {client.lastName}</h4>
                  <span className="client-rut">{client.rut}</span>
                </div>
              </div>
              <div className="client-card-body">
                {client.phoneNumber && <p><strong>Tel:</strong> {client.phoneNumber}</p>}
                {client.email && <p><strong>Email:</strong> {client.email}</p>}
              </div>
              <div className="client-card-footer">
                <Tag color={client.activeSaleProcessesCount > 0 ? 'green' : 'default'}>
                  {client.activeSaleProcessesCount || 0} activas
                </Tag>
                <Tag color="blue">{client.saleProcessesCount || 0} total</Tag>
                {(() => {
                  const config = STATUS_CONFIG[client.overallStatus] || STATUS_CONFIG.sin_operaciones;
                  return <Tag color={config.color}>{config.label}</Tag>;
                })()}
                {client.hasOverdue && (
                  <Tag color="warning" icon={<WarningOutlined />}>Atrasado</Tag>
                )}
              </div>
              {client.allTags && client.allTags.length > 0 && (
                <div className="client-card-tags">
                  {client.allTags.slice(0, 3).map((tag) => (
                    <Tag key={tag} size="small">{tag}</Tag>
                  ))}
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  render () {
    const { clients } = this.props;
    const { filter, viewMode } = this.state;

    let records = clients || [];

    if (filter) {
      records = records.filter(includesFilter(filter, [
        'rut',
        'firstName',
        'lastName',
        'email',
        'phoneNumber'
      ], true));
    }

    const activeCount = records.reduce((sum, c) => sum + (c.activeSaleProcessesCount || 0), 0);
    const totalProcesses = records.reduce((sum, c) => sum + (c.saleProcessesCount || 0), 0);

    return (
      <div style={{ padding: '30px' }}>
        <Row gutter={16} justify="space-between" align="middle" style={{ paddingBottom: '20px' }}>
          <Col>
            <h2 style={{ margin: 0 }}>Clientes</h2>
          </Col>
          <Col>
            <Row gutter={8}>
              <Col>
                <Card size="small" className="counter-card">
                  <span className="counter-label">Clientes</span>
                  <span className="counter-value">{records.length}</span>
                </Card>
              </Col>
              <Col>
                <Card size="small" className="counter-card">
                  <span className="counter-label">Operaciones activas</span>
                  <span className="counter-value">{activeCount}</span>
                </Card>
              </Col>
              <Col>
                <Card size="small" className="counter-card">
                  <span className="counter-label">Total operaciones</span>
                  <span className="counter-value">{totalProcesses}</span>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row gutter={16} align="middle" style={{ paddingBottom: '20px' }}>
          <Col flex="auto">
            <Input
              placeholder="Buscar cliente..."
              addonAfter={<SearchOutlined />}
              value={filter}
              onChange={({ target: { value } }) => this.setState({ filter: value })}
              style={{ width: '348px' }}
            />
          </Col>
          <Col>
            <Radio.Group value={viewMode} onChange={(e) => this.setState({ viewMode: e.target.value })}>
              <Radio.Button value="table"><TableOutlined /> Tabla</Radio.Button>
              <Radio.Button value="cards"><AppstoreOutlined /> Tarjetas</Radio.Button>
            </Radio.Group>
          </Col>
        </Row>

        {viewMode === 'table' ? (
          <Table
            locale={{ emptyText: 'Sin clientes' }}
            showSorterTooltip={false}
            dataSource={records}
            columns={this.tableColumns()}
            rowKey="id"
            rowClassName="cursor-pointer"
            pagination={{ position: ['bottomCenter'], pageSize: 20 }}
            onRow={(record) => ({
              onClick: () => history.push(`/staff/clients/${record.id}`)
            })}
          />
        ) : (
          this.renderCards(records)
        )}
      </div>
    );
  }
}

export default ClientsList;
